import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import {
  T,
  TPlainTextKey,
  TranslationsContext,
  translation,
} from '../../../../../../../Translations';

interface Props {
  title: TPlainTextKey;
  description: TPlainTextKey;
  icon: React.ReactNode;
  onClick: () => void;
  disabled: Maybe<boolean>;
  badge: Maybe<TPlainTextKey>;
  intercomTarget?: string;
}

function TemplateCard({
  title,
  description,
  icon,
  onClick,
  disabled,
  badge,
  intercomTarget,
}: Props) {
  const ts = useContext(TranslationsContext);
  const isDisabled = disabled.getOrElseValue(false);
  const translatedTitle = translation(title)(ts);
  const descriptionId = `template-desc-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-label={`${translatedTitle} section template${isDisabled ? ' (disabled)' : ''}`}
      aria-describedby={descriptionId}
      data-intercom-target={intercomTarget}
      className={`
        relative flex flex-col items-center p-6 border-2 border-border rounded-lg
        transition-all text-center focus:outline-none
        ${
          isDisabled
            ? 'cursor-not-allowed bg-muted/50 opacity-60'
            : 'cursor-pointer hover:border-primary hover:shadow-md bg-card focus:border-primary'
        }
      `}
    >
      {badge.cata({
        Nothing: () => null,
        Just: (b) => (
          <div
            className="absolute top-3 right-3 px-2 py-1 bg-muted rounded"
            style={{ fontSize: 'var(--text-xs)' }}
          >
            <T kind={b} />
          </div>
        ),
      })}

      <div className="mb-4">{icon}</div>

      <h4 className="mb-2" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
        <T kind={title} />
      </h4>

      <p
        id={descriptionId}
        className="text-muted-foreground"
        style={{ fontSize: 'var(--text-sm)' }}
      >
        <T kind={description} />
      </p>
    </button>
  );
}

export default observer(TemplateCard);
